import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useBrandingContext } from '../contexts/BrandingContext'
import LoadingSpinner from '../components/LoadingSpinner'
import SocialAuthButtons from '../components/SocialAuthButtons'
import { getPostAuthPath, withAuthQuery } from '../lib/authRedirect'

function SignupPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const { signup, isAuthenticated } = useAuth()
  const { branding } = useBrandingContext()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (isAuthenticated) {
      navigate(getPostAuthPath(searchParams), { replace: true })
    }
  }, [isAuthenticated, navigate, searchParams])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    // Basic password validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    setIsLoading(true)

    try {
      const result = await signup(email, password)

      if (result.success) {
        navigate(getPostAuthPath(searchParams), { replace: true })
      } else {
        setError(result.error || 'Signup failed')
      }
    } catch (err) {
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Signup Form */}
      <div className="flex-1 flex items-center justify-center py-12 px-8 sm:px-6 lg:px-12 bg-card">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gradient mb-2">
              {branding.site_name}
            </h1>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Create your account
            </h2>
            <p className="text-muted-foreground">
              Start Improving Your Business Performance Today
            </p>
          </div>

          {/* Signup Form */}
          <div className="card">
            <div className="card-body">
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                  <p className="text-sm text-danger-700">{error}</p>
                </div>
              )}

              <SocialAuthButtons disabled={isLoading} />

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-card text-muted-foreground">Or continue with email</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="form-label">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="form-input"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="form-label">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    className="form-input"
                    placeholder="Create a password (min 6 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    className="form-input"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn btn-primary py-3 text-base"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <LoadingSpinner size="sm" className="mr-2" />
                      Creating account...
                    </span>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Login link */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link 
                to={withAuthQuery('/login', searchParams)} 
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Marketing Content */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary-50 to-primary-100 items-center justify-center p-12">
        <div className="max-w-lg text-center">
          {/* SEO Analysis Illustration */}
          <div className="mb-8">
            <div className="relative inline-block">
              <div className="w-64 h-64 bg-card rounded-2xl shadow-2xl flex items-center justify-center">
                <div className="space-y-4 w-full px-8">
                  {/* Mock SEO Dashboard */}
                  <div className="h-4 bg-primary-200 rounded-full w-3/4 mx-auto"></div>
                  <div className="h-3 bg-green-200 rounded-full w-2/3 mx-auto"></div>
                  <div className="h-3 bg-yellow-200 rounded-full w-1/2 mx-auto"></div>
                  <div className="h-3 bg-red-200 rounded-full w-5/6 mx-auto"></div>
                  <div className="flex justify-between mt-6">
                    <div className="w-8 h-8 bg-primary-500 rounded-full"></div>
                    <div className="w-8 h-8 bg-green-500 rounded-full"></div>
                    <div className="w-8 h-8 bg-yellow-500 rounded-full"></div>
                    <div className="w-8 h-8 bg-red-500 rounded-full"></div>
                  </div>
                </div>
              </div>
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                ✓
              </div>
              <div className="absolute -bottom-4 -left-4 w-16 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-lg">
                SEO
              </div>
            </div>
          </div>

          {/* Marketing Copy */}
          <div className="text-center">
            <h3 className="text-3xl font-bold text-foreground mb-4">
              Start your free SEO audit
            </h3>
            <p className="text-xl text-primary-700 font-semibold mb-6">
              <strong>8 actionable fixes waiting inside</strong>
            </p>
            <p className="text-lg text-card-foreground mb-8">
              Create your account and dive in.<br />
              Pay only if you want the complete SEO and AI SEO growth playbook.
            </p>
          </div>

          {/* Features List */}
          <div className="space-y-4 max-w-xs mx-auto">
            <div className="flex items-center text-left">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white text-sm font-bold">✓</span>
              </div>
              <span className="text-card-foreground font-medium">Instant SEO health score</span>
            </div>
            <div className="flex items-center text-left">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white text-sm font-bold">✓</span>
              </div>
              <span className="text-card-foreground font-medium">AI-powered recommendations</span>
            </div>
            <div className="flex items-center text-left">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white text-sm font-bold">✓</span>
              </div>
              <span className="text-card-foreground font-medium">Actionable improvement plan</span>
            </div>
            <div className="flex items-center text-left">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white text-sm font-bold">✓</span>
              </div>
              <span className="text-card-foreground font-medium">Free forever for basic analysis</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignupPage
